import { BuildingKind, Catalogue, charged, priceOf } from '../game/buildings/Catalogue';
import { Materials, countOf } from '../game/buildings/Materials';

/**
 * Skąd kafle biorą grafiki.
 *
 * **Zmniejszone kopie, nie oryginały.** Rysunki są kwadratami po 1024 piksele
 * i megabajcie, a kafel ma ich sto — osiem megabajtów na pierwsze otwarcie
 * zakładki byłoby zapłacone za rozdzielczość, której nie widać. Pełne pliki
 * zostają obok, jako materiał.
 */
const PICTURES = '/assets/pictures/thumbs';

/** Miejsce po obrazku, którego nie ma. */
const EMPTY_SHOT = '<span class="build-shot build-shot-empty" aria-hidden="true"></span>';

/**
 * Rysunek drogi.
 *
 * **Wpisany tutaj, a nie wzięty z katalogu**, bo droga rodzajem budynku nie
 * jest: nie ma wpisu w `buildings`, nie ma szczebli i nie ma bryły — jest
 * linią **przez** pole, a jej przepis (`roads`) mówi o cenie za krawędź.
 * Nazwa pliku leży więc tam, gdzie powstaje jej kafel, tak samo jak nazwa
 * rusztowania leży przy wczytywaniu modeli.
 */
const ROAD_SHOT = 'road-1';

/** Rysunek mostu — z tego samego powodu wpisany tutaj, co rysunek drogi. */
const BRIDGE_SHOT = 'bridge-1';

/**
 * What can be put up, and what it would cost.
 *
 * DOM over the canvas, like the status bar and the compass, and for the same
 * reason: the game renders into a small buffer scaled up by a whole number, and
 * text drawn in there would be a smear. This is interface, not world.
 *
 * **It holds no numbers of its own.** Labels, prices and limits come from the
 * catalogue the server sent; what this file knows is how to lay them out.
 *
 * **Nie przykleja się już do rogu ekranu.** Panel jest zawartością zakładki
 * „Budowanie" w pasku na dole (`HudBar`) i rozwija się nad nim; sam nie wie,
 * gdzie stoi, i nie dokłada się do kontenera. Stąd `element` zamiast wpinania
 * się przy budowie.
 *
 * **Kafel pokazuje rysunek, nie zrzut bryły.** Do tej pory miniatury powstawały
 * z `lookFor` — tej samej funkcji, z której rysuje się budynek na mapie —
 * fotografowanej pod kątem gry przez własny kontekst WebGL. Dziś kafel bierze
 * gotową grafikę z `public/assets/pictures`, bo rysunek mówi o budynku więcej
 * niż jego bryła widziana z góry przez sto pikseli: na kaflu czyta się, **czym
 * ten dom jest**, a nie jaką ma sylwetkę.
 *
 * **Na mapie nic się nie zmienia.** Stoi tam dalej model, a grafika nie ma
 * z nim nic wspólnego poza nazwą pliku — to jest rozjazd, który da się zrobić
 * i którego nie pilnuje nic poza ręką rysującego.
 *
 * **Rodzaj bez grafiki dostaje puste miejsce**, a nie zastępczą bryłę: renderer
 * miniatur zniknął razem z tą zmianą, więc nie ma już czym sfotografować rodzaju,
 * którego nikt jeszcze nie narysował. Puste miejsce, nie brak elementu — kafel
 * niższy od reszty rozjeżdża rząd.
 */

export class BuildPanel {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly hint: HTMLElement;

  private owned = new Map<string, number>();
  /**
   * Czy trwa tryb testowy.
   *
   * Cena na kaflu jest liczona po tej stronie, więc bez tej flagi pokazywałaby
   * liczbę, której serwer już nie bierze — a kafel wyszarzony „brakuje kłód"
   * przy koszcie jednej kłody jest najgorszym możliwym objawem.
   */
  private godmode = false;
  /** Czy trwa kładzenie drogi — wtedy żaden budynek nie jest wybrany. */
  private paving = false;
  private bridging = false;
  /**
   * How many count towards the limit, which is not always how many stand.
   *
   * A camp raised to a village is still owned — it still makes the next hut
   * dearer — but it is no longer a camp, so it no longer fills the slot. Two
   * numbers because they answer two questions.
   */
  private counted = new Map<string, number>();
  private chosen: BuildingKind | null = null;

  constructor(
    private readonly catalogue: Catalogue,
    private readonly materials: Materials,
    private readonly onPick: (kind: BuildingKind | null) => void,
    /**
     * Wejście w kładzenie drogi, i wyjście z niego.
     *
     * Osobne wywołanie, nie kolejny wpis katalogu, bo **droga nie jest
     * budynkiem**: nie ma poziomów, limitu ani pola, na którym stoi. Panel jest
     * tylko miejscem, w którym się o nią prosi.
     */
    private readonly onRoads: (on: boolean) => void = () => {},
    private readonly onBridges: (on: boolean) => void = () => {}
  ) {
    this.root = document.createElement('div');
    this.root.className = 'build';
    this.root.innerHTML = `
      <div class="build-list"></div>
      <div class="build-hint"></div>
    `;

    this.list = this.root.querySelector('.build-list') as HTMLElement;
    this.hint = this.root.querySelector('.build-hint') as HTMLElement;

    this.render();
  }

  /** Element do wpięcia tam, gdzie ma stać — dziś w zakładkę paska HUD. */
  get element(): HTMLElement {
    return this.root;
  }

  /** How many of each kind this player has up, by catalogue key. */
  setOwned(owned: Map<string, number>, counted: Map<string, number>): void {
    this.owned = owned;
    this.counted = counted;
    this.render();
  }

  /** Tryb testowy się przestawił: ceny na kaflach są inne. */
  setGodmode(on: boolean): void {
    if (this.godmode === on) return;
    this.godmode = on;
    this.render();
  }

  /** What the cursor would do, or why it would not. */
  setHint(text: string | null): void {
    this.hint.textContent = text ?? '';
    this.hint.hidden = !text;
  }

  get picked(): BuildingKind | null {
    return this.chosen;
  }

  /** Choose, or drop the choice. Announced either way. */
  pick(kind: BuildingKind | null): void {
    this.chosen = kind;
    // One picked thing at a time: a building under the cursor and a road being
    // drawn would both want the next click.
    if (kind && this.paving) {
      this.paving = false;
      this.onRoads(false);
    }
    if (kind && this.bridging) {
      this.bridging = false;
      this.onBridges(false);
    }
    this.render();
    this.onPick(kind);
    if (!kind) this.setHint(null);
  }

  /** Zacznij albo przerwij kładzenie drogi. */
  paveMode(on: boolean): void {
    this.paving = on;
    if (on && this.chosen) {
      this.chosen = null;
      this.onPick(null);
    }
    if (on && this.bridging) {
      this.bridging = false;
      this.onBridges(false);
    }
    this.render();
    this.onRoads(on);
  }

  /** Tryb mostu — wyklucza drogę i wybrany budynek, jak one siebie nawzajem. */
  bridgeMode(on: boolean): void {
    this.bridging = on;
    if (on && this.chosen) {
      this.chosen = null;
      this.onPick(null);
    }
    if (on && this.paving) {
      this.paving = false;
      this.onRoads(false);
    }
    this.render();
    this.onBridges(on);
  }

  dispose(): void {
    this.root.remove();
  }

  /**
   * Obrazek kafla, albo puste miejsce po nim.
   *
   * Puste, a nie brak: kafle bez obrazka byłyby niższe od reszty i siatka
   * rozjechałaby się w dniu, w którym jednej grafiki zabraknie.
   *
   * **Plik nazywa się tak, jak model pierwszego szczebla** — bo kafel stawia
   * budynek od zera, a `camp-1` jest obozem, nie wioską. Nazwa bierze się
   * z katalogu, czyli z tego samego miejsca, które mówi, którą bryłę wczytać:
   * druga lista nazw rozjechałaby się z pierwszą, a to już raz kosztowało.
   */
  private pictureOf(kind: BuildingKind): string {
    const file = kind.levels[0]?.model ?? kind.model;
    return file
      ? `<img class="build-shot" src="${PICTURES}/${file}.png" alt="" draggable="false">`
      : EMPTY_SHOT;
  }

  private priceWords(kind: BuildingKind): string {
    const owned = this.owned.get(kind.key) ?? 0;
    const price = charged(priceOf(kind.cost, owned), this.godmode);
    if (price === 0 || !kind.cost) return 'za darmo';

    return countOf(this.materials, kind.cost.material, price);
  }

  private render(): void {
    this.list.innerHTML = '';

    for (const kind of this.catalogue.buildings) {
      // A limit that has been reached is shown rather than hidden: an entry
      // that disappears reads as a bug, and "masz już" is an answer.
      const full =
        kind.limit.kind === 'perPlayer' &&
        (this.counted.get(kind.key) ?? 0) >= kind.limit.most;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'build-item';
      button.disabled = full;
      if (this.chosen?.key === kind.key) button.classList.add('is-picked');

      button.innerHTML = `
        ${this.pictureOf(kind)}
        <span class="build-name">${kind.label}</span>
        <span class="build-cost">${full ? 'masz już' : this.priceWords(kind)}</span>
      `;
      // **Grafiki, której nie ma, nie widać** — a nie widać jej jako ikony
      // zepsutego obrazka. Rodzaj ma w katalogu nazwę bryły, ale rysunek do
      // niej nie musi jeszcze istnieć; wtedy kafel zostaje z pustym miejscem,
      // tak samo jak rodzaj bez modelu i jak droga.
      const shot = button.querySelector('img.build-shot');
      shot?.addEventListener(
        'error',
        () => {
          shot.outerHTML = EMPTY_SHOT;
        },
        { once: true }
      );

      button.addEventListener('click', () => {
        this.pick(this.chosen?.key === kind.key ? null : kind);
      });

      this.list.appendChild(button);
    }

    // The road sits at the end of the same row, because it is asked for in the
    // same place and paid for out of the same purse — but it says its price by
    // the cell, since nobody knows how long it will be until it is drawn.
    const road = document.createElement('button');
    road.type = 'button';
    road.className = 'build-item';
    if (this.paving) road.classList.add('is-picked');
    road.innerHTML = `
      <img class="build-shot" src="${PICTURES}/${ROAD_SHOT}.png" alt="" draggable="false">
      <span class="build-name">droga</span>
      <span class="build-cost">${countOf(
        this.materials,
        this.catalogue.roads.material,
        this.catalogue.roads.perEdge
      )} / odcinek</span>
    `;
    const roadShot = road.querySelector('img.build-shot');
    roadShot?.addEventListener(
      'error',
      () => {
        roadShot.outerHTML = EMPTY_SHOT;
      },
      { once: true }
    );

    road.addEventListener('click', () => this.paveMode(!this.paving));
    this.list.appendChild(road);

    // Most obok drogi: to ten sam przepis i ta sama sakwa, tylko cena jest za
    // całość, bo most ma zawsze jedną długość.
    const bridge = document.createElement('button');
    bridge.type = 'button';
    bridge.className = 'build-item';
    if (this.bridging) bridge.classList.add('is-picked');
    bridge.innerHTML = `
      <img class="build-shot" src="${PICTURES}/${BRIDGE_SHOT}.png" alt="" draggable="false">
      <span class="build-name">most</span>
      <span class="build-cost">${countOf(
        this.materials,
        this.catalogue.roads.material,
        charged(this.catalogue.roads.bridge.cost, this.godmode)
      )}</span>
    `;
    const bridgeShot = bridge.querySelector('img.build-shot');
    bridgeShot?.addEventListener(
      'error',
      () => {
        bridgeShot.outerHTML = EMPTY_SHOT;
      },
      { once: true }
    );
    bridge.addEventListener('click', () => this.bridgeMode(!this.bridging));
    this.list.appendChild(bridge);
  }
}
